/**
 * ADR-0243 — typed client for people (per-human visitors).
 *
 *   GET    /v1/sites/:site_id/people?date_from&date_to&page
 *   GET    /v1/sites/:site_id/people/:person_id?page
 *   DELETE /v1/sites/:site_id/people/:person_id
 *
 * Standalone (HttpClient in, typed result out) like `trust-verdicts.ts`, so
 * the visits panel can adopt it without touching the `SillApi` bag.
 */

import { AuthRateLimitError, AuthUnauthorizedError, type PeopleListResponse, type PersonDetailResponse } from '@sill/types/api';

import { ApiError, SUPPORT_READ_ONLY_REASON, SUPPORT_RESTRICTED_REASON, type HttpClient } from './http-client.js';

export interface PeopleQuery {
  /** `YYYY-MM-DD`, inclusive. */
  readonly date_from?: string;
  readonly date_to?: string;
  /** Display zone the dates are days in; sent only with a date, when not UTC. */
  readonly tz?: string;
  /** Search the card's words (browser, OS, device, place). */
  readonly q?: string;
  readonly page?: number;
}

function sitePath(site_id: string): string {
  return `/v1/sites/${encodeURIComponent(site_id)}/people`;
}

export async function listPeople(http: HttpClient, site_id: string, q: PeopleQuery = {}): Promise<PeopleListResponse> {
  const params = new URLSearchParams();
  if (q.date_from !== undefined) params.set('date_from', q.date_from);
  if (q.date_to !== undefined) params.set('date_to', q.date_to);
  if ((q.date_from !== undefined || q.date_to !== undefined) && q.tz !== undefined && q.tz !== 'UTC') params.set('tz', q.tz);
  if (q.q !== undefined && q.q.trim().length > 0) params.set('q', q.q.trim());
  if (q.page !== undefined && q.page > 1) params.set('page', String(q.page));
  const qs = params.toString();
  const res = await http.request<PeopleListResponse>({ method: 'GET', path: `${sitePath(site_id)}${qs ? `?${qs}` : ''}` });
  if (res === undefined || !Array.isArray(res.people)) throw new ApiError('bad_response', null);
  return res;
}

/** `null` when the person is gone (forgotten, retention) or not visible. */
export async function getPerson(http: HttpClient, site_id: string, person_id: string, page = 1): Promise<PersonDetailResponse | null> {
  try {
    const res = await http.request<PersonDetailResponse>({
      method: 'GET',
      path: `${sitePath(site_id)}/${encodeURIComponent(person_id)}${page > 1 ? `?page=${page}` : ''}`,
    });
    if (res === undefined || typeof res.person !== 'object' || !Array.isArray(res.sessions)) {
      throw new ApiError('bad_response', null);
    }
    return res;
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'not_found') return null;
    throw err;
  }
}

export async function forgetPerson(http: HttpClient, site_id: string, person_id: string): Promise<void> {
  await http.request({ method: 'DELETE', path: `${sitePath(site_id)}/${encodeURIComponent(person_id)}` });
}

/** ADR-0243 — the per-site switch. `setting: null` = following the product default. */
export interface VisitorCaptureState {
  readonly enabled: boolean;
  readonly setting: boolean | null;
  readonly default: boolean;
}

function isCaptureState(v: unknown): v is VisitorCaptureState {
  const o = typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : null;
  return o !== null && typeof o['enabled'] === 'boolean' && typeof o['default'] === 'boolean'
    && (o['setting'] === null || typeof o['setting'] === 'boolean');
}

export async function getVisitorCapture(http: HttpClient, site_id: string): Promise<VisitorCaptureState> {
  const res = await http.request<unknown>({ method: 'GET', path: `/v1/sites/${encodeURIComponent(site_id)}/human-visitor-capture` });
  if (!isCaptureState(res)) throw new ApiError('bad_response', null);
  return res;
}

export async function putVisitorCapture(http: HttpClient, site_id: string, enabled: boolean | null): Promise<VisitorCaptureState> {
  const res = await http.request<unknown>({
    method: 'PUT',
    path: `/v1/sites/${encodeURIComponent(site_id)}/human-visitor-capture`,
    body: { enabled },
  });
  if (!isCaptureState(res)) throw new ApiError('bad_response', null);
  return res;
}

/**
 * A short, merchant-facing reason for a failed settings save (founder
 * report 2026-10-14: the toast said only "Could not save", so a network
 * failure, a role refusal and a server error all looked the same).
 */
export function saveFailureReason(err: unknown): string {
  if (err instanceof AuthRateLimitError) return 'Too many requests. Try again in a minute.';
  if (err instanceof AuthUnauthorizedError) return 'Your session has ended. Sign in again.';
  if (!(err instanceof ApiError)) return 'Something went wrong. Try again.';
  if (err.server_reason === SUPPORT_READ_ONLY_REASON || err.server_reason === SUPPORT_RESTRICTED_REASON) return err.message;
  switch (err.kind) {
    case 'network_timeout': return 'This may have saved — refresh to check before trying again.';
    case 'network_error': return 'Sill could not be reached. Check your connection and try again.';
    case 'forbidden': return 'Only an owner or admin can change this setting.';
    case 'not_found': return 'This site was not found.';
    case 'bad_request': return 'Sill did not accept the request.';
    case 'rate_limited': return 'Too many requests. Try again in a minute.';
    case 'service_unavailable': return 'Sill is busy. Try again in a moment.';
    case 'bad_response': return 'Sill sent an unexpected reply.';
    default: return `Sill could not save it (${err.status !== null ? `error ${err.status}` : err.kind}).`;
  }
}
